import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Calendar, MapPin, CheckCircle, Clock, ChevronRight, Lock, Check } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatSurface } from "@/lib/utils";
import { DEFAULT_SLOTS, SLOT_KEYS, SLOT_LABELS, normalizeAnswers } from "@/lib/slots";
import Logo from "@/components/Logo";

export default function OfficialDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeRequest, setActiveRequest] = useState(null);

  const fetchAll = async () => {
    try {
      const [r, e] = await Promise.all([api.get("/me/availability-requests"), api.get("/me/games")]);
      setRequests(r.data.requests);
      setEvents(e.data.events);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/officiel/login", { replace: true });
  };

  const toggleConfirm = async (eventId, gameId, confirmed) => {
    try {
      await api.post(`/me/games/${eventId}/${gameId}/confirm`, { confirmed });
      toast.success(confirmed ? "Assignation confirmée" : "Confirmation retirée");
      fetchAll();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const pending = requests.filter((r) => !r.my_answer);
  const answered = requests.filter((r) => r.my_answer);

  return (
    <div className="min-h-screen bg-[#F5F5F0]" data-testid="official-dashboard-page">
      {activeRequest && (
        <AvailabilityModal
          request={activeRequest}
          onClose={() => setActiveRequest(null)}
          onSaved={() => {
            setActiveRequest(null);
            fetchAll();
          }}
        />
      )}

      <header className="sticky top-0 z-40 backdrop-blur-md bg-[#F5F5F0]/90 border-b border-[var(--border)]">
        <div className="w-full max-w-screen-xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Logo size={36} />
            <span className="ml-2 badge-dark">Officiel</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden sm:block text-right">
              <div className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted)]">Connecté(e) en tant que</div>
              <div className="font-mono text-xs text-black">{user?.first_name} {user?.last_name}</div>
            </div>
            <button
              onClick={handleLogout}
              className="border border-black p-2 hover:bg-[var(--accent)] hover:text-white hover:border-[var(--accent)] transition-colors"
              title="Déconnexion"
              data-testid="official-logout-btn"
            >
              <LogOut size={16} strokeWidth={2.5} />
            </button>
          </div>
        </div>
      </header>

      <main className="w-full max-w-screen-xl mx-auto px-4 md:px-8 py-10 md:py-14">
        <div className="mb-10">
          <div className="section-number">Tableau de bord</div>
          <h1 className="h1-big">Bonjour {user?.first_name}.</h1>
          <p className="text-sm text-[var(--muted)] mt-3 max-w-xl leading-relaxed">
            Réponds aux demandes de disponibilités et consulte les parties qui te sont assignées.
          </p>
        </div>

        {loading ? (
          <p className="font-mono text-xs uppercase tracking-widest text-[var(--muted)] alert-pulse">Chargement…</p>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Disponibilités */}
            <section data-testid="pending-requests-section">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-serif text-3xl">Demandes de dispos</h2>
                {pending.length > 0 && <span className="badge-dark">{pending.length} à répondre</span>}
              </div>

              {pending.length === 0 && answered.length === 0 ? (
                <div className="border-2 border-dashed border-[var(--border-strong)] p-10 text-center">
                  <p className="font-serif text-2xl italic mb-1">Aucune demande</p>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted)]">
                    Quand un gestionnaire t'en envoie une, elle apparaît ici.
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {pending.map((r) => (
                    <button
                      key={r.id}
                      onClick={() => setActiveRequest(r)}
                      className="w-full text-left card-flat card-hover"
                      data-testid={`pending-req-${r.id}`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="badge-dashed">À répondre</span>
                        <ChevronRight size={14} strokeWidth={2.5} />
                      </div>
                      <h3 className="font-serif text-2xl leading-tight">{r.event_name}</h3>
                      <div className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted)] mt-2">
                        {(r.dates || []).join(" · ")}
                      </div>
                      {r.message && <p className="text-xs text-[var(--text)] mt-2 italic line-clamp-2">{r.message}</p>}
                    </button>
                  ))}
                  {answered.map((r) => (
                    <div key={r.id} className="card-flat opacity-80" data-testid={`answered-req-${r.id}`}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="badge inline-flex items-center gap-1"><CheckCircle size={11} strokeWidth={3} /> Répondu</span>
                        {r.locked ? (
                          <span className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted)] inline-flex items-center gap-1" title="Le gestionnaire a verrouillé les modifications">
                            <Lock size={11} strokeWidth={2.5} /> Verrouillé
                          </span>
                        ) : (
                          <button
                            onClick={() => setActiveRequest(r)}
                            className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted)] hover:text-black"
                            data-testid={`modify-req-${r.id}`}
                          >
                            Modifier
                          </button>
                        )}
                      </div>
                      <h3 className="font-serif text-xl">{r.event_name}</h3>
                      <div className="font-mono text-xs text-[var(--muted)] mt-2 space-y-1">
                        {Object.entries(normalizeAnswers(r.my_answer?.answers || {})).map(([d, slotAns]) => {
                          const labelFor = (v) =>
                            v === "yes" ? "DISPO" : v === "no" ? "INDISPO" : null;
                          const parts = SLOT_KEYS
                            .map((sk) => ({ sk, v: slotAns[sk] }))
                            .filter((p) => p.v);
                          if (parts.length === 0) {
                            return (
                              <div key={d}>
                                <span className="text-[var(--muted-2)]">{d}</span> →{" "}
                                <span className="text-[var(--muted-2)] italic">—</span>
                              </div>
                            );
                          }
                          const allSame = parts.length === SLOT_KEYS.length && parts.every((p) => p.v === parts[0].v);
                          return (
                            <div key={d}>
                              <span className="text-[var(--muted-2)]">{d}</span> →{" "}
                              {allSame ? (
                                <span className="text-black font-bold uppercase">{labelFor(parts[0].v)}</span>
                              ) : (
                                <span className="text-black font-bold uppercase">
                                  {parts.map((p, i) => (
                                    <span key={p.sk}>
                                      {i > 0 && " · "}
                                      {SLOT_LABELS[p.sk]}:{labelFor(p.v)}
                                    </span>
                                  ))}
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* Parties assignées */}
            <section data-testid="my-games-section">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-serif text-3xl">Mes parties</h2>
                {events.length > 0 && <span className="badge">{events.reduce((s, e) => s + e.games.length, 0)} parties</span>}
              </div>

              {events.length === 0 ? (
                <div className="border-2 border-dashed border-[var(--border-strong)] p-10 text-center">
                  <p className="font-serif text-2xl italic mb-1">Aucune assignation</p>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted)]">
                    Aucune partie ne t'est assignée pour le moment.
                  </p>
                </div>
              ) : (
                <div className="space-y-5">
                  {events.map((ev) => (
                    <EventGamesCard
                      key={ev.event_id}
                      ev={ev}
                      onToggleConfirm={toggleConfirm}
                    />
                  ))}
                </div>
              )}
            </section>
          </div>
        )}
      </main>
    </div>
  );
}

function EventGamesCard({ ev, onToggleConfirm }) {
  // Grouper les parties par date
  const gamesByDate = useMemo(() => {
    const map = new Map();
    for (const g of (ev.games || [])) {
      const d = g.date || ev.date || "";
      if (!map.has(d)) map.set(d, []);
      map.get(d).push(g);
    }
    // Trier par date croissante + trier games par heure à l'intérieur
    return Array.from(map.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, games]) => [
        date,
        games.sort((x, y) => (x.start_time || "").localeCompare(y.start_time || "")),
      ]);
  }, [ev]);

  const dates = gamesByDate.map(([d]) => d);
  const [activeDate, setActiveDate] = useState(dates[0] || "");
  const showTabs = dates.length > 1;
  const activeGames = showTabs
    ? (gamesByDate.find(([d]) => d === activeDate) || [null, []])[1]
    : (ev.games || []);

  return (
    <div className="card-flat" data-testid={`my-event-${ev.event_id}`}>
      <div className="flex items-center justify-between mb-3 pb-3 border-b border-[var(--border)]">
        <div>
          <h3 className="font-serif text-2xl leading-tight">{ev.name}</h3>
          <div className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted)] mt-1 flex flex-wrap gap-3">
            <span className="inline-flex items-center gap-1"><Calendar size={11} strokeWidth={2.5} />{ev.date}</span>
            {ev.location && <span className="inline-flex items-center gap-1"><MapPin size={11} strokeWidth={2.5} />{ev.location}</span>}
            {ev.closed && <span className="badge-dark !px-1.5">Fermé</span>}
            {!ev.published && <span className="badge-dashed !px-1.5">Brouillon</span>}
          </div>
        </div>
        <span className="font-serif text-3xl tabular leading-none">{(ev.games || []).length}</span>
      </div>

      {/* Onglets par jour (uniquement si plusieurs dates) */}
      {showTabs && (
        <div className="flex flex-wrap gap-1 mb-3 border-b border-[var(--border)] pb-2" data-testid={`day-tabs-${ev.event_id}`}>
          {gamesByDate.map(([date, games]) => {
            const isActive = date === activeDate;
            return (
              <button
                key={date}
                onClick={() => setActiveDate(date)}
                className={`font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 border transition-colors ${
                  isActive
                    ? "bg-black text-white border-black"
                    : "border-[var(--border-strong)] text-[var(--muted)] hover:border-black hover:text-black"
                }`}
                data-testid={`day-tab-${ev.event_id}-${date}`}
              >
                <span>{formatFrenchDayLabel(date)}</span>
                <span className={`ml-1.5 tabular ${isActive ? "opacity-70" : "opacity-60"}`}>({games.length})</span>
              </button>
            );
          })}
        </div>
      )}

      <div className="space-y-2">
        {activeGames.map((g) => (
          <div key={g.id} className="border-b border-[var(--border)] pb-2 last:border-0">
            <div className="flex flex-wrap items-center justify-between text-sm gap-2">
              <div className="flex items-center gap-3 flex-wrap">
                <Clock size={12} strokeWidth={2.5} />
                <span className="font-bold tabular">{g.start_time}–{g.end_time}</span>
                <span className="text-[var(--muted)] font-mono text-xs">Surf {formatSurface(g.surface)}</span>
                {g.category && (
                  <span className="font-mono text-[10px] uppercase tracking-wider border border-black px-1.5 py-0.5" data-testid={`game-category-${g.id}`}>
                    {g.category}
                  </span>
                )}
                {g.phase_name && (
                  <span
                    className="font-mono text-[10px] uppercase tracking-wider px-1.5 py-0.5 bg-black text-white"
                    title="Phase éliminatoire"
                    data-testid={`game-phase-${g.id}`}
                  >
                    {g.phase_name}
                  </span>
                )}
                <span className="font-mono text-xs">{g.team_a || "—"} vs {g.team_b || "—"}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="badge-dark text-[9px]">{g.my_role}</span>
                {g.my_confirmed ? (
                  <button
                    onClick={() => onToggleConfirm(ev.event_id, g.id, false)}
                    style={{ backgroundColor: "#39ff14" }}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md border border-black text-black text-[10px] font-semibold uppercase tracking-wider transition-colors hover:bg-white"
                    title="Annuler la confirmation"
                    data-testid={`unconfirm-game-${g.id}`}
                  >
                    <Check size={11} strokeWidth={3} /> Confirmé
                  </button>
                ) : (
                  <button
                    onClick={() => onToggleConfirm(ev.event_id, g.id, true)}
                    className="btn-secondary !text-[10px] !px-2.5 !py-1"
                    data-testid={`confirm-game-${g.id}`}
                  >
                    <Check size={11} strokeWidth={3} /> Confirmer
                  </button>
                )}
              </div>
            </div>
            {(g.co_officials || []).length > 0 && (
              <div className="mt-1.5 ml-6 flex flex-wrap gap-x-3 gap-y-1 text-xs text-[var(--muted)]" data-testid={`co-officials-${g.id}`}>
                <span className="font-mono text-[10px] uppercase tracking-widest">Avec :</span>
                {g.co_officials.map((co) => (
                  <span key={co.id} className="text-[var(--text)]">
                    <span className="font-mono text-[9px] uppercase tracking-widest text-[var(--muted)] mr-1">{co.role}</span>
                    {co.first_name} {co.last_name}
                  </span>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// Ex: "2026-06-06" → "Samedi 6 juin"
const FR_DAYS = ["Dimanche", "Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi"];
const FR_MONTHS = ["janv", "févr", "mars", "avr", "mai", "juin", "juil", "août", "sept", "oct", "nov", "déc"];

function formatFrenchDayLabel(isoDate) {
  if (!isoDate) return "—";
  const [y, m, d] = isoDate.split("-").map(Number);
  if (!y || !m || !d) return isoDate;
  // Utiliser midi UTC pour éviter les décalages de fuseau
  const dt = new Date(Date.UTC(y, m - 1, d, 12));
  const day = FR_DAYS[dt.getUTCDay()];
  const month = FR_MONTHS[dt.getUTCMonth()];
  return `${day} ${d} ${month}`;
}

function AvailabilityModal({ request, onClose, onSaved }) {
  const dates = useMemo(() => request.dates || [], [request.dates]);
  // Per-date slots map from backend (each date may have its own AM/PM/SOIRÉE hours)
  const slotsPerDate = request.slots_per_date || {};
  const fallbackSlots = request.slots || DEFAULT_SLOTS;
  const slotsFor = (d) => slotsPerDate[d] || fallbackSlots;
  // answers shape: { date: { am: "yes"|"no"|null, pm:..., soiree:... } }
  const initial = useMemo(() => {
    return normalizeAnswers(
      Object.fromEntries(dates.map((d) => [d, request.my_answer?.answers?.[d] ?? null]))
    );
  }, [dates, request]);
  const [answers, setAnswers] = useState(initial);
  const [comment, setComment] = useState(request.my_answer?.comment || "");
  // Hébergement requis pour cet événement (nouveau — mars 2026)
  const [needsLodging, setNeedsLodging] = useState(!!request.my_answer?.needs_lodging);
  const [saving, setSaving] = useState(false);

  const setSlot = (date, slot, value) => {
    setAnswers({ ...answers, [date]: { ...(answers[date] || {}), [slot]: value } });
  };

  const setAllSlotsForDate = (date, value) => {
    setAnswers({ ...answers, [date]: { am: value, pm: value, soiree: value } });
  };

  const allAnswered = dates.every((d) => {
    const a = answers[d] || {};
    return SLOT_KEYS.every((sk) => a[sk] === "yes" || a[sk] === "no");
  });

  const submit = async () => {
    if (!allAnswered) {
      toast.error("Réponds à tous les créneaux pour chaque date");
      return;
    }
    setSaving(true);
    try {
      await api.put(`/me/availabilities/${request.id}`, { answers, comment, needs_lodging: needsLodging });
      toast.success("Disponibilités enregistrées");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/65 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-[var(--surface)] border border-[var(--border-strong)] rounded-2xl p-6 max-w-2xl w-full shadow-2xl my-8" data-testid="availability-modal">
        <div className="mb-5">
          <div className="label">Mes disponibilités</div>
          <h3 className="font-serif text-3xl leading-tight">{request.event_name}</h3>
          {request.message && (
            <p className="text-xs text-[var(--muted)] mt-3 border-l-4 border-black pl-3 italic">{request.message}</p>
          )}
          <p className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted)] mt-3">
            {(() => {
              // If all dates share the same slots, show one summary line; otherwise list per date.
              const allSame = dates.every((d) => {
                const s = slotsFor(d);
                const f = fallbackSlots;
                return SLOT_KEYS.every((sk) => s[sk].start === f[sk].start && s[sk].end === f[sk].end);
              });
              if (allSame) {
                return `Créneaux : AM (${fallbackSlots.am.start}-${fallbackSlots.am.end}) · PM (${fallbackSlots.pm.start}-${fallbackSlots.pm.end}) · SOIRÉE (${fallbackSlots.soiree.start}-${fallbackSlots.soiree.end})`;
              }
              return "Les heures de créneaux peuvent différer selon la date — voir chaque ligne ci-dessous.";
            })()}
          </p>
        </div>
        <div className="space-y-3 mb-4">
          {dates.map((d) => {
            const a = answers[d] || {};
            const dSlots = slotsFor(d);
            return (
              <div key={d} className="border border-black p-3" data-testid={`date-row-${d}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="label">{d}</div>
                  <div className="flex gap-1">
                    <button
                      type="button"
                      onClick={() => setAllSlotsForDate(d, "yes")}
                      className="font-mono text-[9px] uppercase tracking-widest text-[var(--muted)] hover:text-black border border-[var(--border-strong)] px-2 py-0.5"
                      data-testid={`bulk-yes-${d}`}
                    >
                      Tout dispo
                    </button>
                    <button
                      type="button"
                      onClick={() => setAllSlotsForDate(d, "no")}
                      className="font-mono text-[9px] uppercase tracking-widest text-[var(--muted)] hover:text-black border border-[var(--border-strong)] px-2 py-0.5"
                      data-testid={`bulk-no-${d}`}
                    >
                      Tout indispo
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {SLOT_KEYS.map((sk) => (
                    <div key={sk} className="border border-[var(--border-strong)] p-2" data-testid={`slot-${d}-${sk}`}>
                      <div className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted)] mb-1.5">
                        {SLOT_LABELS[sk]} <span className="text-[9px] text-[var(--muted-2)]">{dSlots[sk].start}–{dSlots[sk].end}</span>
                      </div>
                      <div className="grid grid-cols-2 gap-1">
                        {[
                          { v: "yes", l: "OUI" },
                          { v: "no", l: "NON" },
                        ].map((opt) => (
                          <button
                            key={opt.v}
                            type="button"
                            onClick={() => setSlot(d, sk, opt.v)}
                            className={`p-1.5 text-[11px] font-mono uppercase tracking-wider border-2 ${
                              a[sk] === opt.v ? "border-black bg-black text-white" : "border-[var(--border-strong)] text-[var(--muted)] hover:border-black"
                            }`}
                            data-testid={`answer-${d}-${sk}-${opt.v}`}
                          >
                            {opt.l}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
        <div className="mb-5">
          <label className="label block mb-1.5">Hébergement</label>
          <label className="inline-flex items-center gap-2 cursor-pointer" data-testid="needs-lodging-toggle">
            <input
              type="checkbox"
              checked={needsLodging}
              onChange={(e) => setNeedsLodging(e.target.checked)}
              className="w-4 h-4"
              data-testid="needs-lodging-input"
            />
            <span className="text-sm">J&apos;ai besoin d&apos;un hébergement pour cet événement</span>
          </label>
        </div>
        <div className="mb-5">
          <label className="label block mb-1.5">Commentaire (optionnel)</label>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            className="input-box min-h-20"
            placeholder="Ex: dispo uniquement après 14h le samedi…"
            data-testid="availability-comment-input"
          />
        </div>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="btn-secondary">Annuler</button>
          <button onClick={submit} disabled={saving} className="btn-primary" data-testid="availability-save-btn">
            {saving ? "Enregistrement…" : "Enregistrer"}
          </button>
        </div>
      </div>
    </div>
  );
}
